import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import React, { useEffect } from "react";
import { useForm, SubmitHandler, FieldErrors } from "react-hook-form";
import {
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import TruncatedText from "Components/Common/TruncatedText";
import { DrpOption, FormValues } from "../types/user.types";

interface RoleFormValues {
  roleid: number | null;
}

interface Props {
  isOpen: boolean;
  toggle: () => void;
  onSave: (data: FormValues) => void;
  loading?: boolean;
  roleDropdownData: DrpOption[];
  editRecord?: Partial<FormValues> | null;
}

const UpdateRole: React.FC<Props> = ({
  isOpen,
  toggle,
  onSave,
  loading = false,
  roleDropdownData,
  editRecord,
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<RoleFormValues>({
    mode: "onSubmit",
    defaultValues: {
      roleid: null,
    },
  });

  const selectedRoleId = watch("roleid");
  const hasChanges =
    Boolean(selectedRoleId) && selectedRoleId !== (editRecord?.roleid ?? null);

  useEffect(() => {
    if (editRecord) {
      reset({
        roleid: editRecord.roleid ?? null,
      });
    }
  }, [editRecord, reset]);

  const roleFields: FormFieldConfig[] = [
    {
      name: "roleid",
      label: "Role",
      type: "select",
      options: roleDropdownData,
      required: true,
    },
  ];

  const onSubmit: SubmitHandler<RoleFormValues> = (values) => {
    if (values.roleid === null) return;

    onSave({
      userroleid: editRecord?.userroleid ?? null,
      username: editRecord?.username ?? null,
      userid: editRecord?.userid ?? null,
      roleid: values.roleid,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="md"
      backdrop="static"
      centered
      className="common_modal"
    >
      <ModalHeader toggle={toggle} className="fw-bold border-bottom p-3">
        {editRecord?.username ? (
          <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            Update Role
            <TruncatedText text={`(${editRecord.username})`} maxWidth="300px" />
          </span>
        ) : (
          "Update Role"
        )}
      </ModalHeader>

      <ModalBody>
        <form id="updateRoleForm" onSubmit={handleSubmit(onSubmit)}>
          <CommonForm
            columns={1}
            control={control}
            errors={errors as FieldErrors}
            fields={roleFields}
            setValue={setValue}
            getValues={getValues}
          />
        </form>
      </ModalBody>

      <ModalFooter className="bg-white border-top">
        <Button color="dark" outline onClick={toggle}>
          Cancel
        </Button>

        <Button
          color="primary"
          outline
          type="submit"
          form="updateRoleForm"
          disabled={
            loading ||
            isSubmitting ||
            (Boolean(editRecord) && !hasChanges)
          }
        >
          {(loading || isSubmitting) && (
            <Spinner size="sm" className="me-2" />
          )}
          Update
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default UpdateRole;
